import { supabase } from "@/integrations/supabase/client";

const SIGNED_URL_TTL_SECONDS = 60 * 60 * 24 * 7;

type ImageBucket = "forkast_scraping" | "forkast_user";

export interface RecipeImageSourceInput {
  title?: string | null;
  tags?: readonly string[] | null;
  imageUrl?: string | null;
  storageImageUrl?: string | null;
  userImageUrl?: string | null;
  storageImagePath?: string | null;
  userImagePath?: string | null;
}

interface StorageReference {
  bucket: ImageBucket;
  path: string;
}

const SUPPORTED_BUCKETS: ImageBucket[] = ["forkast_user", "forkast_scraping"];

const FALLBACK_CATEGORIES = [
  { category: "breakfast", keywords: ["breakfast", "brunch", "egg", "eggs", "pancake", "cereal", "roesti"] },
  { category: "pasta", keywords: ["pasta", "spaghetti", "spag", "bolognese", "bolo", "pesto", "mac"] },
  { category: "mexican", keywords: ["taco", "tacos", "nachos", "burrito", "enchilada", "tex-mex"] },
  { category: "asian", keywords: ["asian", "chinese", "japanese", "thai", "korean", "stir-fry", "noodle", "pad se ew"] },
  { category: "seafood", keywords: ["fish", "seafood", "salmon", "shrimp", "tuna", "cod"] },
  { category: "soup", keywords: ["soup", "stew", "broth"] },
  { category: "salad", keywords: ["salad", "vegetarian", "vegan", "vegetable", "tomato", "mozzarella"] },
  { category: "pork", keywords: ["pork", "ham", "bacon", "sausage", "sausages", "ribs"] },
  { category: "chicken", keywords: ["chicken", "poultry", "wings"] },
  { category: "beef", keywords: ["beef", "steak"] },
  { category: "baked", keywords: ["baked", "bake", "oven", "gratin", "casserole"] },
  { category: "mediterranean", keywords: ["mediterranean", "greek", "hummus", "falafel", "quark"] },
  { category: "comfort", keywords: ["comfort", "grilled cheese", "mash", "potato", "pizza"] },
] as const;

const isImageBucket = (bucket: string): bucket is ImageBucket => {
  return SUPPORTED_BUCKETS.includes(bucket as ImageBucket);
};

const normalizePath = (path: string | null | undefined): string | null => {
  if (!path) return null;

  const trimmed = path.trim();
  if (!trimmed) return null;

  return trimmed.replace(/^\/+/, "");
};

export const extractStorageReferenceFromUrl = (
  url: string | null | undefined,
  bucketHint?: ImageBucket
): StorageReference | null => {
  if (!url) return null;

  try {
    const urlObj = new URL(url);
    const segments = urlObj.pathname.split("/").filter(Boolean);
    const storageIndex = segments.findIndex((segment) => segment === "storage");
    const versionIndex = segments.findIndex((segment, index) => storageIndex !== -1 && index > storageIndex && segment === "v1");

    if (storageIndex === -1 || versionIndex === -1) {
      return null;
    }

    const modeIndex = segments.findIndex((segment, index) => {
      return index > versionIndex && ["sign", "public", "authenticated"].includes(segment);
    });

    if (modeIndex === -1 || modeIndex >= segments.length - 2) {
      return null;
    }

    const bucket = segments[modeIndex + 1];
    if (!isImageBucket(bucket) || (bucketHint && bucket !== bucketHint)) {
      return null;
    }

    const path = segments
      .slice(modeIndex + 2)
      .map((segment) => decodeURIComponent(segment))
      .join("/");

    return path ? { bucket, path } : null;
  } catch {
    return null;
  }
};

const signStorageReference = async (reference: StorageReference): Promise<string | null> => {
  const { data, error } = await supabase.storage
    .from(reference.bucket)
    .createSignedUrl(reference.path, SIGNED_URL_TTL_SECONDS);

  if (error) {
    console.warn("Failed to sign recipe image URL:", error);
    return null;
  }

  return data.signedUrl;
};

const uniquePush = (items: string[], item: string | null | undefined) => {
  if (item && !items.includes(item)) {
    items.push(item);
  }
};

const getFallbackIllustrationUrl = (source: RecipeImageSourceInput): string | null => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) return null;

  const searchableText = [
    source.title,
    ...(source.tags || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const matchedCategory = FALLBACK_CATEGORIES.find(({ keywords }) => {
    return keywords.some((keyword) => searchableText.includes(keyword));
  })?.category || "default";

  return `${supabaseUrl}/storage/v1/object/public/food-illustrations/${matchedCategory}.png`;
};

export const getRecipeImageUrlCandidates = async (
  source: RecipeImageSourceInput
): Promise<string[]> => {
  const candidates: string[] = [];

  const userImagePath = normalizePath(source.userImagePath);
  const storageImagePath = normalizePath(source.storageImagePath);

  const storageReferences: Array<StorageReference | null> = [
    userImagePath
      ? { bucket: "forkast_user", path: userImagePath }
      : null,
    extractStorageReferenceFromUrl(source.userImageUrl, "forkast_user"),
    storageImagePath
      ? { bucket: "forkast_scraping", path: storageImagePath }
      : null,
    extractStorageReferenceFromUrl(source.storageImageUrl, "forkast_scraping"),
    extractStorageReferenceFromUrl(source.imageUrl),
  ];

  for (const reference of storageReferences) {
    if (!reference) continue;
    uniquePush(candidates, await signStorageReference(reference));
  }

  if (!extractStorageReferenceFromUrl(source.userImageUrl)) {
    uniquePush(candidates, source.userImageUrl);
  }

  if (!extractStorageReferenceFromUrl(source.storageImageUrl)) {
    uniquePush(candidates, source.storageImageUrl);
  }

  if (!extractStorageReferenceFromUrl(source.imageUrl)) {
    uniquePush(candidates, source.imageUrl);
  }

  uniquePush(candidates, getFallbackIllustrationUrl(source));

  return candidates;
};

export const getStoragePathFromUrl = (
  url: string | null | undefined,
  bucketHint?: ImageBucket
): string | null => {
  return extractStorageReferenceFromUrl(url, bucketHint)?.path ?? null;
};
